/**
 * Product V2 API Client
 *
 * API client for V2 product endpoints.
 * Handles product CRUD operations, bulk operations, and status polling.
 */

import AuthClientStore from '../../../../common/services/auth-client.service';
import type {
  ProductV2,
  ProductV2ListResponse,
  BulkOperationsResponse,
  CreateProductV2Payload,
  UpdateProductV2Payload,
} from '../../../../common/models/product-v2.model';

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_PMS_API_URL as string;

// V2 API endpoints
const V2_ROUTES = {
  products: {
    base: '/services/pms/v2/products',
    findOne: (uuid: string) => `/services/pms/v2/products/${uuid}`,
    taxonomyFurniture: '/services/pms/v2/products/taxonomy/furniture',
    bulk: '/services/pms/v2/products/bulk',
    bulkStatus: (batchId: string) => `/services/pms/v2/products/bulk-status/${batchId}`,
    bulkAbort: (batchId: string) => `/services/pms/v2/products/bulk-abort/${batchId}`,
    bulkRetry: (jobId: string) => `/services/pms/v2/products/bulk-retry/${jobId}`,
    groutLayers: (uuid: string) => `/services/pms/v2/products/${uuid}/grout-layers`,
  },
  texturePreviews: '/services/pms/v2/texture-previews',
  photoAnalysis: {
    base: '/services/pms/v2/photo-analysis',
    quote: '/services/pms/v2/photo-analysis/quote',
    findOne: (jobId: string) => `/services/pms/v2/photo-analysis/${jobId}`,
  },
};

// ============================================================================
// Furniture taxonomy
// ============================================================================

export interface FurnitureCategoryOption {
  slug: string;
  displayName: string;
}

export interface FurnitureTaxonomy {
  categories: FurnitureCategoryOption[];
  labelToCategory: Record<string, string>;
}

// ============================================================================
// Types
// ============================================================================

export interface ProductV2QueryParams {
  page?: number;
  limit?: number;
  familySlug?: string;
  productType?: string;
  search?: string;
  isVisible?: boolean;
  active?: boolean;
}

export interface BulkStatusResponse {
  batchId: string;
  status?: string;
  summary: {
    pending: number;
    processing: number;
    generating_seamless?: number;
    completed: number;
    failed: number;
  };
  jobs?: Array<{
    jobId: string;
    uuid?: string;
    operation: string;
    status: string;
    error?: string;
    productData?: Record<string, any>;
    resultProduct?: {
      id: string;
      name: string;
      thumbnailPath?: string;
    };
    errorDetails?: Record<string, any>;
  }>;
  siblingsLinked?: boolean | null;
}

export type BulkStatusCallback = (status: BulkStatusResponse) => void;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: Record<string, any>): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }

  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: any,
  init?: RequestInit
): Promise<T> {
  const authToken = AuthClientStore.getAccessToken();

  const isFormData = body instanceof FormData;

  const headers: Record<string, string> = {
    ...(authToken && { Authorization: `Bearer ${authToken}` }),
    ...(!isFormData && { 'Content-Type': 'application/json' }),
  };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    ...init,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `API request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch the DECRYPTED 3D model for a product (backoffice-only, auth-gated `model-plain`
 * endpoint) and return an object URL a 3D viewer can load. The caller must
 * URL.revokeObjectURL it when done.
 */
export async function getProductModelObjectUrl(uuid: string): Promise<string> {
  const authToken = AuthClientStore.getAccessToken();
  const response = await fetch(`${API_BASE_URL}${V2_ROUTES.products.base}/${uuid}/model-plain`, {
    headers: { ...(authToken && { Authorization: `Bearer ${authToken}` }) },
  });
  if (!response.ok) {
    // Surface the server's message when there is one — "model not found" and
    // "decryption failed" need very different fixes.
    const detail = await response.text().then(
      (t) => { try { return JSON.parse(t)?.message; } catch { return t?.slice(0, 200); } },
      () => undefined,
    );
    throw new Error(`Failed to load 3D model (${response.status})${detail ? `: ${detail}` : ''}`);
  }
  const buffer = await response.arrayBuffer();
  // Verify we actually got a binary glTF before handing it to the loader.
  // Without this a non-GLB payload (an HTML error page, a JSON body, or bytes
  // that are still ciphertext because the key was missing or the path was not
  // recognised as protected) reaches useGLTF, which throws from inside
  // Suspense — and with no error boundary around the R3F canvas that takes
  // down the whole app rather than showing a message in the dialog.
  const magic = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength)));
  if (magic !== 'glTF') {
    throw new Error(
      `Model for ${uuid} is not a valid GLB (got "${magic}"). `
      + 'It may still be encrypted — check ASSET_ENC_KEY on the API.',
    );
  }
  return URL.createObjectURL(new Blob([buffer], { type: 'model/gltf-binary' }));
}
/**
 * Object URL for a 3D model file picked in the form but not uploaded yet. Files only
 * reach the server on save, so before that `model-plain` has nothing to return (404) —
 * the picked file is the model to preview. Validated like the server path above: a file
 * that isn't glTF must fail here with a message, not inside the R3F canvas.
 */
export async function getLocalModelObjectUrl(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const head = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(64, buffer.byteLength)));
  if (head.startsWith('glTF')) {
    return URL.createObjectURL(new Blob([buffer], { type: 'model/gltf-binary' }));
  }
  // A .gltf is JSON. Only a self-contained one previews; external .bin/textures aren't
  // reachable from a blob URL.
  if (head.trimStart().startsWith('{')) {
    return URL.createObjectURL(new Blob([buffer], { type: 'model/gltf+json' }));
  }
  throw new Error(`${file.name} is not a valid glTF/GLB file.`);
}


// ============================================================================
// API Functions
// ============================================================================

/**
 * Get paginated list of products
 */
export async function getProducts(params?: ProductV2QueryParams): Promise<ProductV2ListResponse> {
  const queryString = buildQueryString(params || {});
  return apiRequest<ProductV2ListResponse>('GET', `${V2_ROUTES.products.base}${queryString}`);
}

/**
 * Get single product by UUID (includes populated siblings)
 */
export async function getProduct(uuid: string): Promise<ProductV2> {
  return apiRequest<ProductV2>('GET', V2_ROUTES.products.findOne(uuid));
}

/**
 * Get the canonical furniture taxonomy (category options + label→category map).
 * Single source of truth shared with the visualizer; synced with YOLO labels.
 */
export async function getFurnitureTaxonomy(): Promise<FurnitureTaxonomy> {
  return apiRequest<FurnitureTaxonomy>('GET', V2_ROUTES.products.taxonomyFurniture);
}

/**
 * Create a single product
 */
export async function createProduct(
  data: CreateProductV2Payload,
  files?: { image?: File; thumbnail?: File; material?: File }
): Promise<ProductV2> {
  if (files && Object.values(files).some((f) => f)) {
    // Use FormData for file upload
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));

    if (files.image) {
      formData.append('image', files.image);
    }
    if (files.thumbnail) {
      formData.append('thumbnail', files.thumbnail);
    }
    if (files.material) {
      formData.append('material', files.material);
    }

    return apiRequest<ProductV2>('POST', V2_ROUTES.products.base, formData);
  }

  return apiRequest<ProductV2>('POST', V2_ROUTES.products.base, { data: JSON.stringify(data) });
}

/**
 * Update a single product
 */
export async function updateProduct(
  uuid: string,
  data: UpdateProductV2Payload,
  files?: { image?: File; thumbnail?: File; material?: File }
): Promise<ProductV2> {
  if (files && Object.values(files).some((f) => f)) {
    // Use FormData for file upload
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));

    if (files.image) {
      formData.append('image', files.image);
    }
    if (files.thumbnail) {
      formData.append('thumbnail', files.thumbnail);
    }
    if (files.material) {
      formData.append('material', files.material);
    }

    return apiRequest<ProductV2>('PATCH', V2_ROUTES.products.findOne(uuid), formData);
  }

  return apiRequest<ProductV2>('PATCH', V2_ROUTES.products.findOne(uuid), {
    data: JSON.stringify(data),
  });
}

/**
 * Delete a single product (soft delete)
 */
export async function deleteProduct(uuid: string): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('DELETE', V2_ROUTES.products.findOne(uuid));
}

/**
 * Submit bulk operations (create/update/delete)
 * Returns batch ID for async processing
 */
export async function bulkOperations(formData: FormData): Promise<BulkOperationsResponse> {
  return apiRequest<BulkOperationsResponse>('POST', V2_ROUTES.products.bulk, formData, {
    // Allow large payloads
    // @ts-ignore - custom option for fetch polyfills
    timeout: 2 * 60 * 1000,
  });
}

/**
 * Get status of a bulk operation batch
 */
export async function getBulkStatus(batchId: string): Promise<BulkStatusResponse> {
  return apiRequest<BulkStatusResponse>('GET', V2_ROUTES.products.bulkStatus(batchId));
}

/**
 * Poll bulk batch status until completion
 */
export async function pollBulkStatus(
  batchId: string,
  onProgress?: BulkStatusCallback,
  maxAttempts: number = 300,
  pollInterval: number = 2000,
  expectedJobs?: number
): Promise<BulkStatusResponse> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const status = await getBulkStatus(batchId);

    // Call progress callback
    if (onProgress) {
      onProgress(status);
    }

    // Check if batch is complete (no pending, generating_seamless, or processing jobs)
    const inProgress =
      status.summary.pending +
      (status.summary.generating_seamless || 0) +
      status.summary.processing;

    const totalDone = status.summary.completed + status.summary.failed;

    if (inProgress === 0 && (!expectedJobs || totalDone >= expectedJobs)) {
      // Wait for sibling linking to complete before resolving
      if (status.siblingsLinked !== null && status.siblingsLinked !== undefined) {
        return status;
      }
      // siblingsLinked still null — worker hasn't finished linking, keep polling
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
    attempts++;
  }

  throw new Error(`Batch ${batchId} timed out after ${(maxAttempts * pollInterval) / 1000} seconds`);
}

/**
 * Abort a bulk operation batch
 */
export async function abortBulkBatch(batchId: string): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('POST', V2_ROUTES.products.bulkAbort(batchId));
}

/**
 * Retry a failed bulk job
 */
export async function retryBulkJob(
  jobId: string,
  correctedData?: any
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(
    'POST',
    V2_ROUTES.products.bulkRetry(jobId),
    correctedData ? { correctedData } : undefined
  );
}

/**
 * Queue the grout layers of a product (all its variants whose grout the
 * shopper may change), so the visualizer can change their grout instantly.
 * Variants whose layers are current are left alone.
 */
export async function requestGroutLayers(
  uuid: string
): Promise<{ queued: number; current: number; skipped: number }> {
  return apiRequest('POST', V2_ROUTES.products.groutLayers(uuid));
}

// ============================================================================
// Build from photos (seamless-generator photo analysis)
// ============================================================================

export interface PhotoAnalysisQuote {
  /** What one analysis costs. */
  credits: number;
  /** The vendor's balance. */
  balance: number;
  maxPhotos: number;
}

export type PhotoKind = 'single_tile' | 'tile_sheet' | 'room_scene' | 'seamless_cell' | 'not_a_product';

export interface PhotoAnalysisResult {
  /** The pattern the joints show (or the vendor's, when given). */
  pattern: string | null;
  /** tiles: one image per tile/glaze for the generator; seamless: repeat cells; none: nothing usable. */
  mode: 'tiles' | 'seamless' | 'none';
  images: Array<{ url: string; width: number; height: number }>;
  candidates: Array<{ pattern: string; score: number }>;
  photos: Array<{ index: number; kind: PhotoKind; used: boolean; note: string }>;
  notes: string[];
  /** A seamless cell's size, when the product's size is known. */
  cellSizeCm?: number[] | null;
  /** How the photos lay the tiles (mirror: stack bond, alternate tiles flipped, as cement tiles' rosettes). */
  options?: { mirror?: boolean };
  /** Which images to offer ticked (a room shows the whole collection: the glaze nearest the variant's colour). */
  preselect?: number[] | null;
}

export interface PhotoAnalysisJob {
  jobId: string;
  status: 'pending' | 'processing' | 'done' | 'failed';
  result: PhotoAnalysisResult | null;
  error: string | null;
  credits: number;
}

export interface StartPhotoAnalysisInput {
  files?: File[];
  imageUrls?: string[];
  /** The product's size in cm (width, height): cut-outs keep its proportions. */
  sizeCm?: [number, number];
  /** The product's pattern, when already chosen. */
  pattern?: string;
  /** The variant's colour (#RRGGBB): picks the product's glaze among the ones the photos show. */
  colorHex?: string;
}

export async function getPhotoAnalysisQuote(): Promise<PhotoAnalysisQuote> {
  return apiRequest<PhotoAnalysisQuote>('GET', V2_ROUTES.photoAnalysis.quote);
}

/** Holds the credits, reads the photos in one vision call and queues the computer vision. */
export async function startPhotoAnalysis(input: StartPhotoAnalysisInput): Promise<PhotoAnalysisJob> {
  const formData = new FormData();
  (input.files || []).forEach((file) => formData.append('photos', file));
  if (input.imageUrls?.length) formData.append('imageUrls', JSON.stringify(input.imageUrls));
  if (input.sizeCm) {
    formData.append('sizeWidth', String(input.sizeCm[0]));
    formData.append('sizeHeight', String(input.sizeCm[1]));
  }
  if (input.pattern) formData.append('pattern', input.pattern);
  if (input.colorHex) formData.append('colorHex', input.colorHex);
  return apiRequest<PhotoAnalysisJob>('POST', V2_ROUTES.photoAnalysis.base, formData);
}

export async function getPhotoAnalysis(jobId: string): Promise<PhotoAnalysisJob> {
  return apiRequest<PhotoAnalysisJob>('GET', V2_ROUTES.photoAnalysis.findOne(jobId));
}

// ============================================================================
// Preview in room (a texture tried in the visualizer before it is saved)
// ============================================================================

export interface TexturePreviewInput {
  /** The texture itself (a freshly generated or uploaded one), or… */
  texture?: Blob;
  /** …the URL of a saved one. */
  materialPath?: string;
  /** The texture's size in metres. */
  textureWidth: number;
  textureHeight: number;
  /** The variant it previews (a saved one lends its name and family). */
  variantUuid?: string;
  /** What the visualizer shows and needs to place it. */
  product?: { name?: string; axes?: Record<string, unknown>; familySlug?: string; organization?: { applicationTypes?: string[] } };
}

/** A temporary product the visualizer opens on (`?sku=`), kept a day. */
export async function createTexturePreview(input: TexturePreviewInput): Promise<{ sku: string; uuid: string; alias?: string }> {
  const formData = new FormData();
  if (input.texture) formData.append('texture', input.texture, 'texture.jpg');
  if (input.materialPath) formData.append('materialPath', input.materialPath);
  formData.append('textureWidth', String(input.textureWidth));
  formData.append('textureHeight', String(input.textureHeight));
  if (input.variantUuid) formData.append('variantUuid', input.variantUuid);
  if (input.product) formData.append('product', JSON.stringify(input.product));
  return apiRequest('POST', V2_ROUTES.texturePreviews, formData);
}

// ============================================================================
// Export API object for convenience
// ============================================================================

export const productV2Api = {
  // Read operations
  getProducts,
  getProduct,
  getFurnitureTaxonomy,

  // Write operations
  createProduct,
  updateProduct,
  deleteProduct,

  // Bulk operations
  bulkOperations,
  getBulkStatus,
  pollBulkStatus,
  abortBulkBatch,
  retryBulkJob,

  // Grout layers (grout v2)
  requestGroutLayers,

  // Preview in room
  createTexturePreview,

  // Build from photos
  getPhotoAnalysisQuote,
  startPhotoAnalysis,
  getPhotoAnalysis,
};

export default productV2Api;
